import { memo, type FC } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { CompanyFilters } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";

const OPTIONS = [
  "C-Level",
  "VP",
  "Director",
  "Head",
  "Manager",
  "Senior",
  "Partner",
  "Owner",
  "Founder",
  "Intern",
  "Not Employed",
  "Unknown",
];

type Slice = { include: string[]; exclude: string[] };
const defaultSlice: Slice = { include: [], exclude: [] };

const ContactJobSeniorityFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("contactJobSeniority");
  const { setSlice } = useCompanyFilterActions();

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Include
        </Typography>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={OPTIONS.filter((o) => !slice.exclude.includes(o))}
          value={slice.include}
          onChange={(_, v) =>
            setSlice("contactJobSeniority", {
              ...slice,
              include: v as string[],
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={
                slice.include.length === 0 ? "Search seniority..." : ""
              }
              inputProps={{
                ...params.inputProps,
                "data-testid": "contacts-seniority-include-input",
              }}
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((opt, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} label={opt} size="small" {...tagProps} />;
            })
          }
          size="small"
        />
      </Box>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Exclude
        </Typography>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={OPTIONS.filter((o) => !slice.include.includes(o))}
          value={slice.exclude}
          onChange={(_, v) =>
            setSlice("contactJobSeniority", {
              ...slice,
              exclude: v as string[],
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={
                slice.exclude.length === 0 ? "Search seniority..." : ""
              }
              inputProps={{
                ...params.inputProps,
                "data-testid": "contacts-seniority-exclude-input",
              }}
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((opt, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={opt}
                  size="small"
                  color="error"
                  {...tagProps}
                />
              );
            })
          }
          size="small"
        />
      </Box>
    </Stack>
  );
});

ContactJobSeniorityFilter.displayName = "ContactJobSeniorityFilter";

export const contactJobSeniorityModule: FilterModule<Slice> = {
  key: "contactJobSeniority",
  defaultSlice,
  Component: ContactJobSeniorityFilter,
  toCompanyFilters: (slice): Partial<CompanyFilters> =>
    slice.include.length > 0 || slice.exclude.length > 0
      ? {
          contactFilters: {
            jobSeniority: { include: slice.include, exclude: slice.exclude },
          },
        }
      : {},
  fromCompanyFilters: (filters): Slice => ({
    include: filters.contactFilters?.jobSeniority?.include ?? [],
    exclude: filters.contactFilters?.jobSeniority?.exclude ?? [],
  }),
  getChips: (slice, setSlice) => [
    ...slice.include.map((s) => ({
      id: `contacts-seniority-include-${s}`,
      label: `Seniority: ${s}`,
      filterKey: "contactJobSeniority" as const,
      onRemove: () =>
        setSlice({ ...slice, include: slice.include.filter((x) => x !== s) }),
    })),
    ...slice.exclude.map((s) => ({
      id: `contacts-seniority-exclude-${s}`,
      label: `Excl. Seniority: ${s}`,
      filterKey: "contactJobSeniority" as const,
      onRemove: () =>
        setSlice({ ...slice, exclude: slice.exclude.filter((x) => x !== s) }),
    })),
  ],
  getCount: (slice) => slice.include.length + slice.exclude.length,
};
